/**
 * Optimized Shopify Liquid custom JS
 * Consolidated from 15+ DOMContentLoaded listeners into a single entry point.
 * Key optimizations:
 *   - Single DOMContentLoaded listener
 *   - Body-level MutationObservers replaced with targeted observers
 *   - DOM queries cached and reused
 *   - Link rewriting batched into one pass
 *   - Debounced observer callbacks to prevent cascade loops
 */

(function () {
  'use strict';

  /* ========================================
     UTILITIES
  ======================================== */

  function deferHeavyWork(fn) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 200);
    }
  }

  function debounce(fn, ms) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }

  function parseCountText(text) {
    if (!text) return null;
    var m = text.match(/(\d+)\s+of\s+(\d+)/i);
    return m ? { visible: parseInt(m[1], 10), total: parseInt(m[2], 10) } : null;
  }

  function normalizeVendor(str) {
    return str ? str.toLowerCase().replace(/\s+/g, '').replace(/&/g, '') : '';
  }

  function safeDecodeVendor(raw) {
    var decoded = decodeURIComponent(raw);
    if (decoded.includes('%')) {
      try { decoded = decodeURIComponent(decoded); } catch (e) { /* ignore */ }
    }
    return decoded.replace(/\+/g, ' ').trim();
  }

  /* ========================================
     SINGLE DOMContentLoaded ENTRY POINT
  ======================================== */

  document.addEventListener('DOMContentLoaded', function () {

    var urlParams = new URLSearchParams(window.location.search);
    var isMobile = window.innerWidth < 750;
    var isTabletOrMobile = window.innerWidth < 990;
    var path = window.location.pathname;
    var body = document.body;
    var template = body.dataset.template;
    var collectionTemplate = body.dataset.collectionTemplate;

    var isHomePage = template === 'index';
    var isPublisherPage = template === 'collection' && collectionTemplate === 'publisher';
    var isCartPage = path === '/cart';
    var isSearchPage = !!document.querySelector('.template-search__results');

    var vendorFromURL = urlParams.get('filter.p.vendor');
    var currentVendor = vendorFromURL ? safeDecodeVendor(vendorFromURL) : null;
    var normalizedCurrentVendor = currentVendor ? normalizeVendor(currentVendor) : null;

    var sessionLogo = sessionStorage.getItem('publisherLogoSrc');
    var sessionHref = sessionStorage.getItem('publisherLogoHref');
    var sessionVendor = sessionStorage.getItem('publisherVendor');

    /* ========================================
       1. MEGA MENU — wider columns
    ======================================== */

    (function initWiderMegaMenu() {
      var subMenus = document.querySelectorAll('.mega-menu__list > li > ul');
      for (var i = 0; i < subMenus.length; i++) {
        var ul = subMenus[i];
        if (ul.querySelectorAll(':scope > li').length > 8) {
          ul.classList.add('two-column-menu');
          var parent = ul.closest('li');
          if (parent) parent.classList.add('wide-column-parent');
        }
      }
    })();

    /* ========================================
       2. FOOTER ACCORDION (mobile only)
    ======================================== */

    if (isMobile) {
      (function initFooterAccordion() {
        var buttons = document.querySelectorAll('.footer-accordion__title');
        for (var i = 0; i < buttons.length; i++) {
          buttons[i].addEventListener('click', function () {
            var expanded = this.getAttribute('aria-expanded') === 'true';
            this.setAttribute('aria-expanded', !expanded);
            var content = this.closest('.footer-block').querySelector('.footer-accordion__content');
            if (content) content.classList.toggle('active', !expanded);
          });
        }
      })();
    }

    /* ========================================
       3. MEGA MENU — horizontal scroll/slide
    ======================================== */

    (function initMegaMenuScroll() {
      var containers = document.querySelectorAll('.mega-menu__scroll-container');

      for (var c = 0; c < containers.length; c++) {
        (function (container) {
          var wrapper = container.querySelector('.mega-menu__scroll-wrapper');
          var prev = container.querySelector('.mega-menu__nav--prev');
          var next = container.querySelector('.mega-menu__nav--next');
          var details = container.closest('details');

          if (!wrapper || !prev || !next || !details) return;

          function updateArrows() {
            var maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
            var scrollable = wrapper.scrollWidth > wrapper.clientWidth;
            prev.style.display = scrollable ? 'flex' : 'none';
            next.style.display = scrollable ? 'flex' : 'none';
            prev.disabled = wrapper.scrollLeft <= 0;
            next.disabled = wrapper.scrollLeft >= maxScroll - 1;
          }

          function scrollWrapper(direction) {
            var observer = new MutationObserver(function (mutations) {
              for (var i = 0; i < mutations.length; i++) {
                if (mutations[i].attributeName === 'open' && !details.hasAttribute('open')) {
                  details.setAttribute('open', '');
                }
              }
            });
            observer.observe(details, { attributes: true });

            wrapper.scrollBy({ left: 300 * direction, behavior: 'smooth' });
            if (!details.hasAttribute('open')) details.setAttribute('open', '');
            setTimeout(function () { observer.disconnect(); }, 500);
          }

          [prev, next].forEach(function (btn) {
            btn.addEventListener('click', function (e) {
              e.stopPropagation();
              scrollWrapper(btn === prev ? -1 : 1);
            });
            btn.addEventListener('mousedown', function (e) { e.stopPropagation(); });
          });

          wrapper.addEventListener('mousedown', function (e) { e.stopPropagation(); });
          wrapper.addEventListener('click', function (e) { e.stopPropagation(); });
          wrapper.addEventListener('scroll', updateArrows);
          window.addEventListener('resize', debounce(updateArrows, 150));

          updateArrows();
        })(containers[c]);
      }

      document.addEventListener('click', function (e) {
        var openMenus = document.querySelectorAll('details.mega-menu[open]');
        for (var i = 0; i < openMenus.length; i++) {
          if (!openMenus[i].contains(e.target)) {
            openMenus[i].removeAttribute('open');
          }
        }
      });
    })();

    /* ========================================
       4. PUBLISHER SESSION PERSISTENCE
    ======================================== */

    (function initPublisherSession() {
      var logoImg = document.getElementById('header-logo-img');
      var logoLink = document.getElementById('header-logo');
      if (!logoImg || !logoLink) return;

      if (isHomePage) {
        sessionStorage.removeItem('publisherLogoSrc');
        sessionStorage.removeItem('publisherLogoHref');
        sessionStorage.removeItem('publisherVendor');
        sessionLogo = null;
        sessionHref = null;
        sessionVendor = null;
      }

      if (isPublisherPage) {
        sessionStorage.setItem('publisherLogoSrc', logoImg.src);
        sessionStorage.setItem('publisherLogoHref', logoLink.href);
        sessionLogo = logoImg.src;
        sessionHref = logoLink.href;

        var match = logoLink.href.match(/\/collections\/([^/?]+)/);
        var slug = match ? match[1] : null;
        if (slug) {
          var vendorName = slug
            .replace(/-%26-/g, ' & ')
            .replace(/-/g, ' ')
            .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
          sessionStorage.setItem('publisherVendor', vendorName);
          sessionVendor = vendorName;
        }
      }

      if (!isHomePage && !isPublisherPage && sessionLogo && sessionHref) {
        logoImg.src = sessionLogo;
        logoLink.href = sessionHref;
      }

      if (sessionLogo && !isHomePage) {
        document.querySelectorAll('ul.list-menu--inline > li').forEach(function (li) {
          var span = li.querySelector('summary span');
          if (span && span.textContent.trim() === 'Publisher') li.classList.add('hidden');
        });
        document.querySelectorAll('ul.menu-drawer__menu summary').forEach(function (summary) {
          if (summary.textContent.trim().startsWith('Publisher')) {
            var parentLi = summary.closest('li');
            if (parentLi) parentLi.classList.add('hidden');
          }
        });
      }

      if (sessionLogo && sessionHref && sessionVendor) {
        body.classList.add('has-vendor');
      }

      // Footer copyright link
      if (sessionHref) {
        var cleanHref = sessionHref.split('?')[0];
        var footerLink = document.querySelector('small.copyright__content a');
        if (footerLink) {
          footerLink.href = cleanHref;
          new MutationObserver(function () {
            if (footerLink.href !== cleanHref) footerLink.href = cleanHref;
          }).observe(footerLink, { attributes: true, attributeFilter: ['href'] });
        }
      }
    })();

    /* ========================================
       5. CART PAGE — vendor filtering & URL
    ======================================== */

    if (isCartPage && sessionVendor) {
      (function initCartPage() {
        var url = new URL(window.location.href);
        var updated = false;

        if (url.searchParams.get('view') !== 'publisher') {
          url.searchParams.set('view', 'publisher');
          updated = true;
        }
        if (url.searchParams.get('filter.p.vendor') !== sessionVendor) {
          url.searchParams.set('filter.p.vendor', sessionVendor);
          updated = true;
        }
        if (updated) {
          window.location.replace(url.toString());
          return;
        }

        // Filter cart products by vendor
        document.querySelectorAll('ul.grid.product-grid li').forEach(function (li) {
          var v = li.dataset.vendor;
          if (v) li.style.display = v.trim() === sessionVendor.trim() ? '' : 'none';
        });

        // Update internal links
        document.querySelectorAll('a[href]').forEach(function (link) {
          var href = link.getAttribute('href');
          if (!href.startsWith('http') && !href.startsWith('//')) {
            var linkUrl = new URL(href, window.location.origin);
            linkUrl.searchParams.set('view', 'publisher');
            linkUrl.searchParams.set('filter.p.vendor', sessionVendor);
            link.setAttribute('href', linkUrl.pathname + linkUrl.search + linkUrl.hash);
          }
        });
      })();
    }

    /* ========================================
       6. BODY CLASS FOR VENDOR PAGES
    ======================================== */

    if (currentVendor) {
      var safeVendorClass = currentVendor
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '')
        .toLowerCase();
      body.classList.add('has-vendor', 'vendor-' + safeVendorClass);
    }

    /* ========================================
       7. COLLECTION MENU LINK REWRITING
          (single pass for all link updates)
    ======================================== */

    if (currentVendor) {
      (function rewriteCollectionLinks() {
        var isViewPublisher = urlParams.get('view') === 'publisher';
        var allCollectionLinks = document.querySelectorAll('a[href*="/collections/"]');

        for (var i = 0; i < allCollectionLinks.length; i++) {
          var a = allCollectionLinks[i];
          if (a.id === 'header-logo') continue;

          var href = a.getAttribute('href');
          if (!href || href.includes('filter.p.vendor')) continue;

          try {
            var urlObj = new URL(href, window.location.origin);
            if (!urlObj.pathname.startsWith('/collections/') || urlObj.pathname.includes('/products/')) continue;

            urlObj.searchParams.set('filter.p.vendor', currentVendor);
            urlObj.searchParams.set('view', 'publisher');
            a.href = urlObj.pathname + '?' + urlObj.searchParams.toString();
          } catch (e) { /* skip malformed URLs */ }
        }
      })();
    }

    /* ========================================
       8. FACET FILTERS (desktop & mobile combined)
       Deferred to avoid blocking initial page paint
    ======================================== */

    if (currentVendor) {
      var vendorTotalCount = null;
      var desktopCountEl = document.querySelector('#ProductCountDesktop');
      var mobileCountEl = document.querySelector('#ProductCount');
      var isInitialCountRun = true;

      function getFilteredTotal() {
        if (!desktopCountEl) return vendorTotalCount || 0;
        var match = desktopCountEl.textContent.match(/(\d+)\s+of\s+(\d+)/);
        return match ? parseInt(match[1], 10) : (vendorTotalCount || 0);
      }

      function handleDesktopFacets() {
        if (isTabletOrMobile) return;

        document.querySelectorAll('.facets__disclosure-vertical.js-filter').forEach(function (details) {
          var summaryText = (details.querySelector('summary span') || {}).textContent || '';
          var isVendorFilter = summaryText.trim().startsWith('Publisher');

          details.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
            var li = input.closest('li.list-menu__item');
            if (!li) return;

            var countMatch = (li.querySelector('span[aria-hidden="true"]') || {}).textContent;
            var count = countMatch ? (countMatch.match(/\((\d+)\)/) || [])[1] : null;
            if (count !== null && parseInt(count, 10) === 0) li.style.display = 'none';

            if (isVendorFilter) {
              if (input.value !== currentVendor) {
                li.style.display = 'none';
              } else {
                input.checked = true;
                li.style.pointerEvents = 'none';
                li.style.opacity = '0.6';
              }
            }
          });
        });
      }

      function handleMobileFacets() {
        if (!isTabletOrMobile) return;

        document.querySelectorAll('.mobile-facets__item').forEach(function (item) {
          var input = item.querySelector('input[type="checkbox"]');
          if (!input) return;

          if (input.name === 'filter.p.vendor') {
            if (input.value !== currentVendor) {
              item.style.display = 'none';
            } else {
              input.checked = true;
              item.style.opacity = '0.6';
              item.style.pointerEvents = 'none';
            }
          }

          var label = item.querySelector('span[aria-hidden="true"]');
          var countStr = label ? (label.textContent.match(/\((\d+)\)/) || [])[1] : null;
          if (countStr !== null && parseInt(countStr, 10) === 0) item.style.display = 'none';
        });
      }

      function handleFacetRemoveButtons() {
        document.querySelectorAll('facet-remove').forEach(function (facet) {
          var a = facet.querySelector('a.active-facets__button');
          if (a && a.textContent.includes(currentVendor)) {
            a.href = 'javascript:void(0)';
            a.style.cursor = 'default';
            a.style.opacity = '0.6';
            facet.style.pointerEvents = 'none';
            var svg = a.querySelector('svg.icon-close-small');
            if (svg) svg.style.display = 'none';
          }
        });

        document.querySelectorAll('facet-remove a.active-facets__button-remove').forEach(function (a) {
          a.style.display = 'none';
        });
      }

      function updateProductCounts() {
        var productGrid = document.querySelector('#product-grid');
        if (!productGrid || vendorTotalCount === null) return;

        if (isInitialCountRun) {
          isInitialCountRun = false;
          return;
        }

        var visible = 0;
        productGrid.querySelectorAll('.grid__item').forEach(function (card) {
          if (card.offsetParent !== null) visible++;
        });

        var total = getFilteredTotal();
        var text = visible + ' of ' + total + ' product' + (total !== 1 ? 's' : '');

        if (desktopCountEl) desktopCountEl.textContent = text;
        if (mobileCountEl) mobileCountEl.textContent = text;

        var mobileFacetsCount = document.querySelector('.mobile-facets__count');
        if (mobileFacetsCount) mobileFacetsCount.textContent = text;
      }

      function fixPaginationLinks() {
        document.querySelectorAll('.pagination a').forEach(function (a) {
          var href = a.getAttribute('href');
          if (!href) return;
          try {
            var urlObj = new URL(href, window.location.origin);
            if (!urlObj.pathname.startsWith('/collections/')) return;
            urlObj.searchParams.set('filter.p.vendor', currentVendor);
            urlObj.searchParams.set('view', 'publisher');
            a.href = urlObj.pathname + '?' + urlObj.searchParams.toString();
          } catch (e) { /* skip */ }
        });
      }

      function facetUpdate() {
        handleDesktopFacets();
        handleMobileFacets();
        handleFacetRemoveButtons();
        fixPaginationLinks();
        updateProductCounts();
      }

      deferHeavyWork(function () {
        facetUpdate();

        if (desktopCountEl) {
          var parsed = parseCountText(desktopCountEl.textContent);
          if (parsed && Number.isFinite(parsed.total)) {
            vendorTotalCount = parsed.total;
            isInitialCountRun = false;
            facetUpdate();
          } else {
            var countObserver = new MutationObserver(function () {
              var p = parseCountText(desktopCountEl.textContent);
              if (p && Number.isFinite(p.total)) {
                vendorTotalCount = p.total;
                countObserver.disconnect();
                isInitialCountRun = false;
                facetUpdate();
              }
            });
            countObserver.observe(desktopCountEl, { childList: true, subtree: true, characterData: true });
          }
        }

        document.addEventListener('facets:updated', debounce(facetUpdate, 100));

        var facetContainers = [
          document.querySelector('.active-facets'),
          document.querySelector('.facets__display-vertical'),
          document.querySelector('#product-grid')
        ].filter(Boolean);

        var debouncedFacetUpdate = debounce(facetUpdate, 150);

        setTimeout(function () {
          facetContainers.forEach(function (container) {
            new MutationObserver(debouncedFacetUpdate).observe(container, {
              childList: true,
              subtree: false
            });
          });
        }, 500);
      });
    }

    /* ========================================
       9. MEGA MENU — vendor filtering
          (desktop and account/login pages)
          Deferred to avoid blocking initial paint
    ======================================== */

    deferHeavyWork(function () {
      var vendor = currentVendor;
      var normalizedVendor = normalizedCurrentVendor;

      var accountPaths = ['/account/login', '/account/logout', '/account'];
      if (!vendor && sessionVendor && accountPaths.indexOf(path) !== -1) {
        vendor = sessionVendor;
        normalizedVendor = normalizeVendor(sessionVendor);
      }

      if (!vendor) return;

      var megaMenus = document.querySelectorAll('.mega-menu');
      megaMenus.forEach(function (menu) {
        var classSafeVendor = vendor.replace(/\s+/g, '-');
        menu.classList.add('mega-menu--vendor-active', 'mega-menu--vendor-' + classSafeVendor);

        var columnsWrapper = menu.querySelector('.mega-menu__columns');
        if (!columnsWrapper) return;

        var originalColumns = Array.from(columnsWrapper.querySelectorAll('.mega-menu__column'));
        if (originalColumns.length === 0) {
          var col = document.createElement('ul');
          col.className = 'mega-menu__column';
          columnsWrapper.appendChild(col);
          originalColumns.push(col);
        }

        var visibleItems = [];

        originalColumns.forEach(function (column) {
          var items = column.querySelectorAll('li.wide-column-parent, li.grandchild-item, li[data-vendors]');
          items.forEach(function (item) {
            var rawVendors = item.dataset.vendors || '';
            var vendorsArray = rawVendors.split(',').map(function (v) { return normalizeVendor(v); });

            if (vendorsArray.indexOf(normalizedVendor) !== -1) {
              visibleItems.push(item);

              if (accountPaths.indexOf(path) !== -1) {
                var link = item.querySelector('a[href]');
                if (link) {
                  var origVendors = rawVendors.split(',').map(function (v) { return v.trim(); });
                  var matchingVendor = origVendors.find(function (v) {
                    return normalizeVendor(v) === normalizedVendor;
                  }) || origVendors[0];

                  var encodedVendor = matchingVendor.replace(/&/g, '%26').replace(/\s+/g, '+');
                  var linkUrl = new URL(link.href, window.location.origin);
                  link.href = linkUrl.pathname + '?view=publisher&filter.p.vendor=' + encodedVendor + linkUrl.hash;
                }
              }
            } else {
              item.remove();
            }
          });

          column.innerHTML = '';
        });

        var columnIndex = 0;
        visibleItems.forEach(function (item, i) {
          if (i % 10 === 0 && i > 0) columnIndex++;

          if (!originalColumns[columnIndex]) {
            var newCol = document.createElement('ul');
            newCol.className = 'mega-menu__column';
            columnsWrapper.appendChild(newCol);
            originalColumns.push(newCol);
          }

          originalColumns[columnIndex].appendChild(item);
        });

        originalColumns.forEach(function (col) {
          col.style.display = col.children.length === 0 ? 'none' : 'flex';
        });
      });
    });

    /* ========================================
       10. MOBILE MENU — vendor filtering
    ======================================== */

    if (isTabletOrMobile && currentVendor) {
      var menuItems = document.querySelectorAll('.menu-drawer__menu li[data-vendors]');
      menuItems.forEach(function (item) {
        var vendors = (item.dataset.vendors || '').split(',').map(function (v) { return v.trim(); });
        item.style.display = vendors.indexOf(currentVendor) !== -1 ? 'list-item' : 'none';
      });
    }

    /* ========================================
       11. SEARCH RESULTS — vendor filtering
    ======================================== */

    if (isSearchPage) {
      (function initSearchFilter() {
        var searchContainer = document.querySelector('.template-search__results');
        if (!searchContainer) return;

        var vendorParam = urlParams.get('options[prefix1]');
        if (!vendorParam) return;

        var decodedVendor = safeDecodeVendor(vendorParam);
        var encodedVendor = encodeURIComponent(decodedVendor).replace(/%20/g, '+');

        function filterSearchResults() {
          var items = searchContainer.querySelectorAll('li[data-vendor]');
          items.forEach(function (li) {
            var liVendor = (li.getAttribute('data-vendor') || '').trim();
            if (liVendor === decodedVendor) {
              li.style.display = '';
              // Append vendor params to product links
              li.querySelectorAll("a[href^='/products/']").forEach(function (a) {
                var href = a.getAttribute('href');
                if (href.includes('filter.p.vendor=')) return;
                var sep = href.includes('?') ? '&' : '?';
                a.setAttribute('href', href + sep + 'view=publisher&filter.p.vendor=' + encodedVendor);
              });
            } else {
              li.style.display = 'none';
            }
          });

          var countBox = document.querySelector('.product-count-vertical');
          var countBoxMobile = document.querySelector('.product-count.medium-hide.large-up-hide h2.product-count__text');
          if (countBox) countBox.style.display = 'none';
          if (countBoxMobile) countBoxMobile.style.display = 'none';
        }

        // Delayed initial run (Shopify loads search results via AJAX)
        setTimeout(filterSearchResults, 400);

        // Targeted observer on search container only (not body)
        new MutationObserver(debounce(filterSearchResults, 200)).observe(searchContainer, {
          childList: true,
          subtree: true
        });
      })();
    }

    /* ========================================
       12. SEARCH FORM — hidden vendor inputs
    ======================================== */

    (function initSearchFormVendorInputs() {
      var rawVendor = urlParams.get('filter.p.vendor') || urlParams.get('options[prefix1]');
      if (!rawVendor) return;

      var decodedVendor = safeDecodeVendor(rawVendor);

      var forms = document.querySelectorAll(
        'nav.header__inline-menu .search-modal__form, ' +
        '.search-modal__content .search-modal__form, ' +
        '.template-search__search form.search'
      );

      forms.forEach(function (form) {
        // options[prefix1]
        var optInput = form.querySelector("input[name='options[prefix1]']");
        if (!optInput) {
          optInput = document.createElement('input');
          optInput.type = 'hidden';
          optInput.name = 'options[prefix1]';
          form.appendChild(optInput);
        }
        optInput.value = decodedVendor.replace(/ /g, '+');

        // filter.p.vendor
        var vendorInput = form.querySelector("input[name='filter.p.vendor']");
        if (!vendorInput) {
          vendorInput = document.createElement('input');
          vendorInput.type = 'hidden';
          vendorInput.name = 'filter.p.vendor';
          form.appendChild(vendorInput);
        }
        vendorInput.value = decodedVendor;
      });
    })();

    /* ========================================
       13. PRODUCT PAGE — recommendation filtering
    ======================================== */

    if (currentVendor && path.startsWith('/products/')) {
      (function initProductRecommendations() {
        function filterRecommendations() {
          var recSection = document.querySelector('product-recommendations');
          if (!recSection) return;

          recSection.querySelectorAll('li.grid__item[data-vendor]').forEach(function (item) {
            var itemVendor = (item.dataset.vendor || '').replace(/&amp;/g, '&').trim();
            if (itemVendor !== currentVendor) {
              item.style.display = 'none';
            } else {
              item.style.display = '';
              item.querySelectorAll('a').forEach(function (a) {
                try {
                  var linkURL = new URL(a.href, window.location.origin);
                  linkURL.searchParams.set('filter.p.vendor', vendorFromURL);
                  a.href = linkURL.toString();
                } catch (e) { /* skip */ }
              });
            }
          });
        }

        filterRecommendations();

        // Targeted observer: only watch the recommendations section, not body
        var recSection = document.querySelector('product-recommendations');
        if (recSection) {
          new MutationObserver(debounce(filterRecommendations, 200)).observe(recSection, {
            childList: true,
            subtree: true
          });
        } else {
          // Recommendations may not exist yet — watch for it to appear (shallow body observer)
          var bodyObserver = new MutationObserver(function () {
            var section = document.querySelector('product-recommendations');
            if (section) {
              bodyObserver.disconnect();
              filterRecommendations();
              new MutationObserver(debounce(filterRecommendations, 200)).observe(section, {
                childList: true,
                subtree: true
              });
            }
          });
          bodyObserver.observe(body, { childList: true, subtree: false });
        }
      })();
    }

    /* ========================================
       14. CLEAR SEARCH BUTTON
    ======================================== */

    (function initClearSearch() {
      var inputIDs = ['Search-In-Template', 'Search-In-Modal', 'Search-In-Modal-1'];
      var inputs = inputIDs.map(function (id) { return document.getElementById(id); }).filter(Boolean);
      var clearButtons = document.querySelectorAll('.clear-search-btn');
      var logoLink = document.getElementById('header-logo');

      if (inputs.length === 0 || clearButtons.length === 0 || !logoLink) return;

      function toggleClearButton() {
        var hasValue = inputs.some(function (input) { return input.value.trim(); });
        clearButtons.forEach(function (btn) { btn.style.display = hasValue ? 'block' : 'none'; });
      }

      inputs.forEach(function (input) { input.addEventListener('input', toggleClearButton); });
      toggleClearButton();

      clearButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          inputs.forEach(function (input) { input.value = ''; });
          toggleClearButton();
          var redirectUrl = logoLink.getAttribute('href');
          if (redirectUrl) window.location.href = redirectUrl;
        });
      });
    })();

    /* ========================================
       15. SEARCH BAR URL REFRESH
    ======================================== */

    (function initSearchBarRefresh() {
      var searchInput = document.getElementById('Search-In-Modal');
      var logoLink = document.getElementById('header-logo');
      if (!searchInput || !logoLink) return;

      function cleanSearchAndRedirect() {
        if (searchInput.value.trim() !== '') return;

        var url = new URL(window.location.href);
        var removed = false;
        ['q', 'from', 'options[prefix]'].forEach(function (param) {
          if (url.searchParams.has(param)) {
            url.searchParams.delete(param);
            removed = true;
          }
        });

        if (removed || url.pathname.includes('/search')) {
          window.location.href = logoLink.getAttribute('href');
        }
      }

      searchInput.addEventListener('input', cleanSearchAndRedirect);
      searchInput.addEventListener('search', cleanSearchAndRedirect);
    })();

    /* ========================================
       16. LOGOUT REDIRECT TO PUBLISHER PAGE
    ======================================== */

    (function initLogoutRedirect() {
      var logoutBtn = document.getElementById('cognito-logout');
      if (!logoutBtn || !sessionHref) return;

      logoutBtn.addEventListener('click', function () {
        window.location.href = sessionHref;
      });
    })();

    /* ========================================
       17. HEADER SCROLL (Safari fix)
    ======================================== */

    (function initHeaderScroll() {
      var header = document.querySelector('.shopify-section-header-sticky');
      if (!header) return;

      var lastScrollY = window.pageYOffset;
      var ticking = false;

      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          var currentScrollY = window.pageYOffset;
          if (currentScrollY <= 0) {
            header.classList.remove('header--hidden');
          } else if (currentScrollY > lastScrollY) {
            header.classList.add('header--hidden');
          } else {
            header.classList.remove('header--hidden');
          }
          lastScrollY = currentScrollY;
          ticking = false;
        });
      }, { passive: true });
    })();

  }); // end DOMContentLoaded

})(); // end IIFE
